import {ArrUtil, Option} from 'yougile-util'
import {
  DataObjectId,
  DeadlineStickerValue,
  extractStickerState,
  getChecklists,
  Hub,
  isSetSticker,
  mapStickerState,
  noBoardContainerId,
  SandboxTree,
  StickerValue,
  TaskData,
  TimerStickerValue,
  TimetrackerValue
} from 'yougile-model'
import {SyncEvents} from '@client/sync-events'
import {Accounts} from '../client/accounts'
import {Sticker, stickerCtl, SystemCtlDict} from './sticker/sticker'
import {SubtaskUtil} from './subtask-util'
import {Task} from './task'

const taskDataFromHubCache = new Map<DataObjectId, TaskData>()
const eventListenerCompanies = new Set<DataObjectId>()

export const invalidateTaskDataCache = (id: DataObjectId): void => {
  taskDataFromHubCache.delete(id)
}

/**
 * Returns array of ids: project id, board id and column id
 * if the task is on board. If the task is without board then
 * it returns `noBoardContainerId`. For subtasks that are not
 * on board it returns empty array
 */
export const getLocations = (
  box: SandboxTree,
  task: Hub | undefined
): DataObjectId[] => {
  if (!task) {
    return []
  }

  if (task.location === noBoardContainerId) {
    const isSubtask = SubtaskUtil.getParentTaskIds(task.id).length > 0
    return isSubtask ? [] : [noBoardContainerId]
  }

  return box
    .getAllParentHubs(task)
    .map(h => h.id)
    .filter(id => id !== box.companyId)
}

export const getLocationsAtDepth = (
  box: SandboxTree,
  task: Hub | undefined,
  depth: number
): DataObjectId[] => {
  if (!task) {
    return []
  }
  if (depth < 0) {
    return []
  }
  if (depth === 0) {
    return getLocations(box, task)
  }
  const res = new Set<DataObjectId>()
  const parents = SubtaskUtil.getParentTaskIds(task.id)
  for (const parentId of parents) {
    const parent = box.getHub(parentId)
    for (const id of getLocationsAtDepth(box, parent, depth - 1)) {
      res.add(id)
    }
  }
  return [...res]
}

export const taskDataFromHub = (box: SandboxTree, task: Hub): TaskData => {
  const taskDataFromCache = taskDataFromHubCache.get(task.id)
  if (taskDataFromCache) {
    return taskDataFromCache
  }
  if (!eventListenerCompanies.has(box.companyId)) {
    eventListenerCompanies.add(box.companyId)
    SyncEvents.addListener(box.companyId, '*', () => {
      taskDataFromHubCache.clear()
    })
  }

  const taskWithLocation = SubtaskUtil.getFirstParentWithLocation(task)

  const pr = box.getProjectFor(task)
  const visiblePr = box.getProjectFor(taskWithLocation)
  if (!pr) {
    return undefined
  }

  const checklistsItems = getChecklists(task)
  const checklists = {
    total: 0,
    completed: 0,
    allTexts: ''
  }
  if (checklistsItems) {
    checklistsItems.forEach(list => {
      if (!list?.items) {
        return
      }
      checklists.total += list.items.length
      checklists.completed += list.items.filter(i => i?.isCompleted).length
      checklists.allTexts +=
        `${list.title}: ${list.items.map(x => x?.title ?? '').join('; ')}` + ' '
    })
  }

  const stage = Task.getGTDStage(task)
  const creator = Task.creator(task)

  const getSticker = (stickerName: keyof SystemCtlDict) => {
    const sticker = stickerCtl[stickerName].getSticker(box)
    if (!sticker || !sticker.id) {
      return 'not-pinned'
    }
    const state = task.stickers[sticker.id]
    if (state === '') {
      return 'not-set'
    }
    if (!state) {
      if (stickerName === 'Timetracking') {
        return stickerCtl.Timetracking.isOnTask(task) ? 'not-set' : 'not-pinned'
      } else {
        return 'not-pinned'
      }
    }
    return state
  }

  const hist = task.getLocHistory()
  const moveTimes = new Set<number>()
  for (const loc of Object.keys(hist)) {
    for (const time of hist[loc]) {
      if (time) {
        moveTimes.add(time)
      }
    }
  }
  moveTimes.delete(task.timestamp)

  const deadline = getSticker('Deadline') as StickerValue<DeadlineStickerValue>
  const timetracking = getSticker(
    'Timetracking'
  ) as StickerValue<TimetrackerValue>
  const timer = getSticker('Timer') as StickerValue<TimerStickerValue>
  const stopwatch = getSticker('Stopwatch') as StickerValue<TimerStickerValue>
  const lastMessage = task.chat.lastMessage()
  const assigned = Sticker.getUsers(task)
  const assignedNames = assigned
    .map(uid => Accounts.getName(uid))
    .filter(a => !!a)
  const createdByName = Option.from(creator)
    .map(c => Accounts.getName(c.id))
    .getOrElse('')
  const noBoard = taskWithLocation.location === noBoardContainerId

  const visibleColumn = noBoard
    ? undefined
    : (box.get(taskWithLocation.location) as Hub)
  const visibleBoard = noBoard
    ? undefined
    : (box.get(box.get(taskWithLocation.location).location) as Hub)

  const column = noBoard ? undefined : (box.get(task.location) as Hub)
  const board = noBoard
    ? undefined
    : (box.get(box.get(task.location).location) as Hub)

  const assignedBy = Object.keys(task.data.assignedBy ?? {}) as DataObjectId[]
  const parentTaskIds = SubtaskUtil.getParentTaskIds(task.id)

  const taskData: TaskData = {
    id: task.id,
    companyId: task.companyId,
    title: task.title,
    description: task.description,
    columnId: task.location,
    columnTitle: noBoard || column.title === column.id ? '' : column.title,
    visibleColumnTitle:
      noBoard || visibleColumn.title === visibleColumn.id
        ? ''
        : visibleColumn.title,
    boardId: box.get(task.location).location,
    boardTitle: noBoard || board.title === board.id ? '' : board.title,
    visibleBoardTitle:
      noBoard || visibleBoard.title === visibleBoard.id
        ? ''
        : visibleBoard.title,
    projectId: pr.id,
    projectTitle: noBoard || pr.title === pr.id ? '' : pr.title,
    visibleProjectTitle:
      noBoard || visiblePr.title === visiblePr.id ? '' : visiblePr.title,
    subtasks: task.data?.subtaskList,
    parentTaskIds,
    locations1: parentTaskIds.length ? getLocationsAtDepth(box, task, 1) : [],
    locationsOther: parentTaskIds.length
      ? ArrUtil.union(
          getLocationsAtDepth(box, task, 2),
          getLocationsAtDepth(box, task, 3)
        )
      : [],
    createTime: task.timestamp,
    createdBy: creator ? creator.id : undefined,
    assignedBy: assignedBy,
    createdByName,
    assigned,
    assignedNames,
    archived: Task.isArchived(task),
    assignTime: assigned
      ? Math.max(+task.data.assignTime || 0, task.timestamp)
      : undefined,
    completeTime:
      stage === 'done'
        ? Math.max(Task.getGTDLastChangeTime(task) || 0, task.timestamp)
        : undefined,
    checklists,
    moveTimes: [...moveTimes],
    gtdStage: stage,
    stickers: task.stickers,
    lastMessageTime: lastMessage ? lastMessage.timestamp : undefined,
    messagesCount: task.chat.getMessagesCount(),
    timetracking,
    timer: mapStickerState(timer, t => t.current),
    stopwatch: mapStickerState(stopwatch, s => s.current),
    deadline: mapStickerState(deadline, d => d.current?.deadline),
    timelineDepth: extractStickerState(
      mapStickerState(deadline, d => d.current?.order)
    ),
    startDate: mapStickerState(deadline, d => d.current?.startDate),
    deadlineWithTime:
      deadline &&
      isSetSticker(deadline) &&
      deadline.current &&
      deadline.current.withTime,
    dependencies: task.data.dependencies || {},
    mirrors: task.data?.mirrors
  }
  taskDataFromHubCache.set(task.id, taskData)

  return taskData
}
